<template>
  <v-container>
    <v-snackbar
      v-model="snack"
      :timeout="3000"
      top
      :color="snackColor"
      class="text-capitalize"
    >
      {{ snackText }}
      <v-btn @click="snack = false" outlined>
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-slide-y-transition>
      <v-app-bar v-if="loaded && editing" app dense collapse-on-scroll>
        <v-spacer></v-spacer>
        <v-btn @click="resetForm" icon>
          <v-icon large>settings_backup_restore</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click="updateImmo" :disabled="!valid" icon color="primary">
          <v-icon large>save</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
      </v-app-bar>
    </v-slide-y-transition>

    <v-tabs
      v-model="activeTab"
      fixed-tabs
      align-with-title
      icons-and-text
      class="elevation-4"
    >
      <v-tabs-slider color="accent"></v-tabs-slider>
      <v-tab href="#info">
        Info
        <v-icon>info</v-icon>
      </v-tab>
      <v-tab href="#images">
        Images
        <v-icon>photo</v-icon>
      </v-tab>
      <v-tab href="#map">
        Map
        <v-icon>map</v-icon>
      </v-tab>
      <v-tab href="#notes">
        Notes
        <v-icon>event_note</v-icon>
      </v-tab>
      <v-tab href="#delete">
        Delete
        <v-icon>warning</v-icon>
      </v-tab>

      <v-tabs-items v-model="activeTab" touchless>
        <v-tab-item value="info">
          <v-container>
            <v-form ref="immoForm" v-model="valid">
              <v-row>
                <v-col>
                  <v-text-field
                    v-if="editImmo.meta.ref"
                    :value="editImmo.meta.ref"
                    label="Ref. No"
                    readonly
                    outlined
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    v-model="editImmo.meta.ref_ext"
                    label="Ref (ext)"
                  />
                </v-col>
                <v-col>
                  <div><v-icon>created</v-icon>{{editImmo.created_at | formatDate }}</div>
                  <div><v-icon>updated</v-icon>{{editImmo.updated_at | formatDate }}</div>
                </v-col>
              </v-row>

              <v-text-field v-model="editImmo.info.title" label="Title" />
              <v-select
                :items="categories"
                v-model="editImmo.meta.categories"
                :rules="rules.categories"
                label="Categories"
                chips
                multiple
              ></v-select>
              <v-select
                :items="payment"
                v-model="editImmo.meta.payment"
                :rules="rules.payment"
                label="Payment"
                chips
              ></v-select>
              <v-select
                :items="status"
                v-model="editImmo.meta.status"
                :rules="rules.status"
                label="Status"
                chips
              ></v-select>

              <v-textarea
                readonly
                auto-grow
                rows="2"
                :value="editImmo.location.address"
                label="Location"
                clearable
                clear-icon="close"
                @click:clear="clearLocation"
              >
                <template v-slot:prepend>
                  <LocationPicker
                    :location="immo.location"
                    @new_location="setLocation"
                  />
                </template>
              </v-textarea>

              <v-row>
                <v-col>
                  <v-text-field
                    v-model.number="editImmo.properties.price"
                    label="Price"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="editImmo.properties.living"
                    label="Living Area"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="editImmo.properties.bedrooms"
                    label="Bedrooms"
                    type="number"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="editImmo.properties.lawn"
                    label="Lawn"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    v-model.number="editImmo.properties.build"
                    label="Build Year"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="editImmo.properties.property"
                    label="Property Area"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="editImmo.properties.bathrooms"
                    label="Bathrooms"
                    type="number"
                  ></v-text-field>

                  <v-text-field
                    v-model.number="editImmo.properties.terrace"
                    label="Terrace"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-switch
                    label="Pool"
                    v-model="editImmo.properties.pool"
                  ></v-switch>

                  <v-switch
                    label="Parking"
                    v-model="editImmo.properties.parking"
                  ></v-switch>
                  <v-switch
                    label="Garage"
                    v-model="editImmo.properties.garage"
                  ></v-switch>
                  <v-switch
                    label="Rental Licence"
                    v-model="editImmo.properties.rental"
                  ></v-switch>
                </v-col>
                <v-col>
                  <v-switch
                    label="Sea Access"
                    v-model="editImmo.properties.sea_access"
                  ></v-switch>
                  <v-switch
                    label="Sea View"
                    v-model="editImmo.properties.sea_view"
                  ></v-switch>
                  <v-switch
                    label="Golf"
                    v-model="editImmo.properties.golf"
                  ></v-switch>
                  <v-switch
                    label="Investment"
                    v-model="editImmo.properties.investment"
                  ></v-switch>
                </v-col>
              </v-row>

              <v-textarea
                v-model="editImmo.info.description"
                label="Description"
                auto-grow
              />
            </v-form>
          </v-container>
        </v-tab-item>

        <v-tab-item value="images">
          <v-toolbar dense>
            <v-toolbar-items>
              <v-dialog
                v-if="images.length > 0"
                v-model="gallery"
                transition="dialog-bottom-transition"
                @keydown.esc="close"
              >
                <template v-slot:activator="{ on }">
                  <v-icon large v-on="on">view_carousel</v-icon>
                </template>
                <v-card>
                  <v-toolbar dense>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                      <v-btn icon @click="gallery = false">
                        <v-icon>close</v-icon>
                      </v-btn>
                    </v-toolbar-items>
                  </v-toolbar>

                  <v-carousel
                    height="auto"
                    hide-delimiters
                    show-arrows-on-hover
                  >
                    <v-carousel-item
                      v-for="image in images"
                      :key="image.id"
                      :src="imageURL(image.filename)"
                    ></v-carousel-item>
                  </v-carousel>
                </v-card>
              </v-dialog>
            </v-toolbar-items>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <Uppy
                :modelId="immo.id"
                modelClass="immo"
                :endpoint="imageUploadURL()"
                @imageUploadSuccess="addImage"
              />
            </v-toolbar-items>
          </v-toolbar>

          <v-container fluid>
            <v-row dense>
              <v-col v-for="img in images" :key="img.id" cols="6">
                <v-card max-width="300px">
                  <v-img :src="imageThumbURL(img.filename)" height="200px">
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="grey lighten-2"
                        >
                        </v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="setImmoThumb(img.filename)" icon>
                      <v-icon
                        :color="
                          thumb === img.filename.replace(/scaled/, 'thumbnail')
                            ? 'teal'
                            : 'grey'
                        "
                        >photo_library</v-icon
                      >
                    </v-btn>

                    <v-btn @click="deleteImage(img)" icon>
                      <v-icon>delete_forever</v-icon>
                    </v-btn>

                    <a :href="imageURL(img.filename)" download>
                      <v-btn icon>
                        <v-icon>cloud_download</v-icon>
                      </v-btn>
                    </a>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <v-tab-item value="map">
          <SingleImmo :location="immo.location" />
        </v-tab-item>

        <v-tab-item value="notes">
          <v-container>
            <v-form ref="noteForm">
              <v-text-field
                v-model="editNote.ContactName"
                label="Contact Name"
              />
              <v-text-field
                v-model="editNote.ContactEmail"
                label="Contact Email"
              />
              <v-text-field
                v-model="editNote.ContactPhone"
                label="Contact Phone"
              />
              <v-textarea
                v-model="editNote.Comment"
                label="Comment"
                auto-grow
              />
            </v-form>
          </v-container>
        </v-tab-item>

        <v-tab-item value="delete">
          <v-toolbar>
            <v-spacer></v-spacer>
            <v-btn @click="deleteImmo" color="error">Delete Immo</v-btn>
          </v-toolbar>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </v-container>
</template>

<script>
import ImmoService from '@/services/immo.service'
import LocationPicker from '@/components/map/LocationPicker'
import SingleImmo from '@/components/map/SingleImmo'

import Uppy from '@/components/helper/Uppy.vue'

export default {
  name: 'immoEdit',
  components: {
    LocationPicker,
    SingleImmo,
    Uppy
  },
  data() {
    return {
      loaded: false,
      gallery: false,
      immo: {
        meta: {},
        info: {},
        location: {},
        properties: {}
      },
      editImmo: {
        meta: { payment: 'BUY' },
        info: {},
        location: {},
        properties: {}
      },
      editingImmo: false,
      valid: true,
      rules: {
        categories: [
          v => v.length > 0 || 'Select one or more categories',
          () => !this.errors.categories || this.errors.categories
        ],
        payment: [
          v => !!v || 'Select payment type',
          () => !this.errors.payment || this.errors.payment
        ],
        status: [
          v => !!v || 'Select status type',
          () => !this.errors.status || this.errors.status
        ]
      },

      note: {},
      editNote: {},
      editingNote: false,
      images: [],
      errors: {},

      activeTab: 'info',
      snack: false,
      snackColor: '',
      snackText: '',

      categories: [
        'VILLA',
        'APARTMENT',
        'FINCA',
        'PENTHOUSE',
        'COMMUNITY',
        'BUSINESS',
        'PLOT'
      ],
      status: ['INPROGRESS', 'AUTHORISED', 'CONTACT', 'UNKNOWN', 'SOLD'],
      payment: ['BUY', 'RENT']
    }
  },
  computed: {
    editing() {
      return this.editingImmo || this.editingNote
    },
    thumb() {
      return !!this.editImmo.info && (this.editImmo.info.thumb || '')
    }
  },

  watch: {
    editImmo: {
      handler() {
        this.editingImmo = true
      },
      deep: true
    },
    editNote: {
      handler() {
        this.editingNote = true
      },
      deep: true
    },
    'editImmo.properties.garage'(newVal) {
      if (newVal) {
        this.editImmo.properties.parking = true
      }
    }
  },

  methods: {
    resetForm() {
      this.loaded = false
      this.editImmo = JSON.parse(JSON.stringify(this.immo))
      this.editNote = JSON.parse(JSON.stringify(this.note))
      this.errors = {}
      setTimeout(() => {
        this.editingImmo = false
        this.editingNote = false
        this.loaded = true
      }, 500)
    },
    setLocation(loc) {
      this.editImmo.location = loc
    },
    clearLocation() {
      this.editImmo.location.address = ''
      this.editImmo.location.lat = 0
      this.editImmo.location.lng = 0
      //this.editImmo.location = {}
    },

    async getImmo() {
      const id = parseInt(this.$route.params['id'], 10)
      if (Number.isInteger(id)) {
        try {
          ;[this.immo, this.note, this.images] = await Promise.all([
            ImmoService.getImmo(id),
            ImmoService.getNote(id),
            ImmoService.getImages(id)
          ])
          this.resetForm()
        } catch (error) {
          console.log(error)
          console.log('could not get immo data')
        }
      } else {
        this.$router.push('/')
      }
    },

    async updateImmo() {
      if (this.$refs.immoForm.validate()) {
        try {
          if (this.editingImmo) {
            this.errors = {}
            this.immo = await ImmoService.updateImmo(this.editImmo)
            this.editingImmo = false
          }

          if (this.editingNote) {
            this.errors = {}
            // if (this.$refs.noteForm.validate()) {
            this.note = await ImmoService.updateNote(
              this.immo.id,
              this.editNote
            )
            this.editingNote = false
            // }
          }
          this.snackSuccess('immo updated')
        } catch (error) {
          console.log(error)
          this.errors = { ...error.fields }
          this.$refs.immoForm.validate()
          this.snackError(error.message)
        }
      }
    },
    async deleteImmo() {
      if (
        await this.$root.$confirm('Delete? Are you sure?', '', {
          color: 'red'
        })
      ) {
        try {
          await ImmoService.deleteImmo(this.immo.id)
          this.snackSuccess('immo deleted')
          this.$router.replace('/list')
        } catch (error) {
          console.log(error.message)
          this.snackError(error.message)
        }
      }
    },

    async deleteImage(i) {
      if (
        i.id &&
        (await this.$root.$confirm('Delete', 'Are you sure?', {
          color: 'red'
        }))
      ) {
        try {
          await ImmoService.deleteImage(this.immo.id, i.id)
          this.images = this.images.filter(v => v.id !== i.id)
          this.snackSuccess('image deleted')
        } catch (error) {
          console.log(error.message)
          this.snackError(error.message)
        }
      }
    },

    imageURL(filename) {
      return `/images/${filename}`
    },
    imageThumbURL(filename) {
      return `/images/${filename.replace(/scaled/, 'thumbnail')}`
    },

    imageUploadURL() {
      return `${process.env.VUE_APP_ROOT_API}/api/immo/${this.immo.id}/images`
    },

    setImmoThumb(filename) {
      const newThumb = filename.replace(/scaled/, 'thumbnail')
      if (this.thumb === newThumb) return
      this.editImmo.info.thumb = newThumb
      this.editingImmo = true
    },

    addImage(image) {
      this.images.push(image)
      if (!this.editImmo.info.thumb) {
        this.setImmoThumb(image.filename)
        this.editingImmo = true
      }
    },

    onResize() {
      this.$refs.map.mapObject.invalidateSize()
    },

    snackSuccess(message) {
      this.snackColor = 'success'
      this.snackText = message
      this.snack = true
    },
    snackError(message) {
      this.snackColor = 'error'
      this.snackText = message
      this.snack = true
    }
  },
  created() {
    this.getImmo()
  }
}
</script>
